import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { securityService } from '../services/apiServices';
import { Permission, UserPermissions } from '../types';

export const queryKeys = {
  permissions: ['permissions'] as const,
  users: ['security', 'users'] as const,
  userPermissions: (userId: number) => ['security', 'users', userId, 'permissions'] as const,
  statistics: ['security', 'statistics'] as const,
};

// Get all permissions grouped by resource
export const usePermissions = () => {
  return useQuery<Permission[]>({
    queryKey: queryKeys.permissions,
    queryFn: () => securityService.getPermissions(),
  });
};

// Get all users with their permissions
export const useSecurityUsers = () => {
  return useQuery<UserPermissions[]>({
    queryKey: queryKeys.users,
    queryFn: () => securityService.getUsers(),
  });
};

// Get specific user's permissions
export const useUserPermissions = (userId: number) => {
  return useQuery<UserPermissions>({
    queryKey: queryKeys.userPermissions(userId),
    queryFn: () => securityService.getUserPermissions(userId),
    enabled: !!userId,
  });
};

// Get permission statistics
export const usePermissionStatistics = () => {
  return useQuery({
    queryKey: queryKeys.statistics,
    queryFn: () => securityService.getStatistics(),
  });
};

// Assign permissions to user
export const useAssignPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, permissionIds }: { userId: number; permissionIds: number[] }) =>
      securityService.assignPermissions(userId, permissionIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
      queryClient.invalidateQueries({ queryKey: queryKeys.userPermissions(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.statistics });
    },
  });
};

// Grant single permission
export const useGrantPermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, permissionId }: { userId: number; permissionId: number }) =>
      securityService.grantPermission(userId, permissionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
      queryClient.invalidateQueries({ queryKey: queryKeys.userPermissions(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.statistics });
    },
  });
};

// Revoke single permission
export const useRevokePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, permissionId }: { userId: number; permissionId: number }) =>
      securityService.revokePermission(userId, permissionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
      queryClient.invalidateQueries({ queryKey: queryKeys.userPermissions(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.statistics });
    },
  });
};

// Assign resource permissions
export const useAssignResourcePermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      resource,
      permissionIds,
    }: {
      userId: number;
      resource: string;
      permissionIds: number[];
    }) => securityService.assignResourcePermissions(userId, resource, permissionIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
      queryClient.invalidateQueries({ queryKey: queryKeys.userPermissions(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.statistics });
    },
  });
};

// Revoke resource permissions
export const useRevokeResourcePermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, resource }: { userId: number; resource: string }) =>
      securityService.revokeResourcePermissions(userId, resource),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
      queryClient.invalidateQueries({ queryKey: queryKeys.userPermissions(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.statistics });
    },
  });
};

// Toggle user active/disabled
export const useToggleUserActive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => securityService.toggleActive(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
    },
  });
};
